import Flex from '@Components/Bases/Flex';
import { FormInput } from '@Components/Form';
import FormRadio from '@Components/Form/FormRadio';
import FormSelect from '@Components/Form/FormSelect';
import {
  disabledFormInputStyles,
  disabledFormSelectStyles,
} from '@Components/Form/styles';
import { useGetInfoCategoryMasterData } from '@Domains/MasterData';
import {
  Alert,
  Box,
  InputLabel,
  MenuItem,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { IGreenhouseFormValues } from '@Router/EsgGreenhouse/Containers/EsgGreenhouseFormProvider/types';
import { arrange, asc, tidy } from '@tidyjs/tidy';
import { setBooleanOption } from '@Utils/boolean';
import { Control, useFormContext, useWatch } from 'react-hook-form';
import { OrganizationType } from 'src/Enum/EsgGreenhouse';
import { INFO_CATEGORY_MASTER_DATA } from 'src/Enum/MasterData';
import { booleanOptions } from '../../NetZero/ModalNetZero/utils';

type NeutralityVerificationFormvalProps = {
  control: Control<IGreenhouseFormValues, unknown>;
};

const NeutralityVerification = ({
  control,
}: NeutralityVerificationFormvalProps) => {
  const [{ data = [], loading, error }] = useGetInfoCategoryMasterData(
    INFO_CATEGORY_MASTER_DATA.DEPT_CARBON_NEUTRAL
  );

  const organizationOptions = tidy(
    data,
    arrange(['sequence', asc('sequence')])
  );
  const { setValue, watch, unregister, trigger } = useFormContext();
  const [organizationId] = watch([
    'organizationId',
    'organizationTh',
    'organizationEn',
  ]);
  const [isGuarantee] = watch(['isGuarantee']);
  const disabledSelectOrganization = !isGuarantee || isGuarantee === 'false';
  const disabledInputOrganization =
    !organizationId ||
    organizationId === OrganizationType.GHG_MGNT_ORGANIZATION;

  const completedSection =
    useWatch({
      name: 'greenhouseGoalTerm.completedSection',
      control,
    }) || undefined;

  const { isCompletedTh, isCompletedEn } = completedSection ?? {};

  const resetOrganizationFields = () => {
    unregister('organizationId');
    unregister('organizationTh');
    unregister('organizationEn');
    setValue('organizationId', '');
    setValue('organizationTh', '');
    setValue('organizationEn', '');
  };

  const handleGuaranteeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    const formatToBoolean = setBooleanOption(value);

    setValue('isGuarantee', formatToBoolean);

    if (value === 'false') {
      resetOrganizationFields();
    } else {
      resetOrganizationFields();
    }
  };

  if (loading) {
    return (
      <Box mt={2}>
        <Typography>Loading ...</Typography>
      </Box>
    );
  }

  const handleOrganizationChange = (event: SelectChangeEvent<unknown>) => {
    const selectedId = Number(event.target.value);
    setValue('organizationId', selectedId);

    if (selectedId === OrganizationType.GHG_MGNT_ORGANIZATION) {
      const selectedOption = organizationOptions.find(
        (option) => option.id === selectedId
      );
      if (selectedOption) {
        unregister('organizationId');
        setValue('organizationId', OrganizationType.GHG_MGNT_ORGANIZATION);

        setValue('organizationTh', selectedOption.nameTh);
        setValue('organizationEn', selectedOption.nameEn);
        trigger('organizationTh');
        trigger('organizationEn');
      }
    } else {
      unregister('organizationTh');
      setValue('organizationTh', '');
      unregister('organizationEn');
      setValue('organizationEn', '');
    }
  };

  if (error) {
    return (
      <Box mt={2}>
        <Alert severity={'error'}>
          ไม่สามารถโหลดข้อมูลรายชื่อองค์กรหรือสถาบันที่รับรองความเป็นกลางทางคาร์บอนของบริษัท
        </Alert>
      </Box>
    );
  }

  return (
    <Flex flexDirection="column" sx={{ mt: 4 }}>
      <Typography fontWeight={800} mb={2}>
        การรับรองความเป็นกลางทางคาร์บอน (Carbon Neutrality)
      </Typography>
      <Typography sx={{ display: 'inline', fontSize: 12, mt: -2 }}>
        (บริษัทที่ผ่านการรับรองผลการชดเชยประเภทองค์กรเท่านั้น)
      </Typography>

      <Flex sx={{ mt: 2 }} gap={2}>
        <Flex justifyContent={'end'} minWidth="350px">
          <InputLabel
            sx={{
              textAlign: 'right',
              marginRight: 1,
              width: '100%',
            }}
            required
          >
            การรับรองความเป็นกลางทางคาร์บอนสำหรับองค์กร
          </InputLabel>
        </Flex>

        <Flex width={'100%'}>
          <FormRadio
            disabled={isCompletedTh || isCompletedEn}
            sx={{ flexDirection: 'column' }}
            name="isGuarantee"
            options={booleanOptions}
            row={true}
            onChange={handleGuaranteeChange}
          />
        </Flex>
      </Flex>

      <Flex sx={{ mt: 2 }} gap={2} width={'100%'}>
        <Flex justifyContent={'end'} minWidth={'350px'} pl={8}>
          <InputLabel
            sx={{
              textAlign: 'right',
              marginRight: 1,
              minWidth: '250px',
              wordWrap: 'break-word',
              overflowWrap: 'break-word',
              whiteSpace: 'normal',
            }}
          >
            รายชื่อองค์กรหรือสถาบันที่รับรองความเป็นกลางทางคาร์บอนของบริษัท
          </InputLabel>
        </Flex>

        <Flex width={'100%'}>
          <FormSelect
            name="organizationId"
            placeholder="เลือกรายชื่อ"
            fullWidth
            sx={
              disabledSelectOrganization || isCompletedTh || isCompletedEn
                ? disabledFormSelectStyles
                : undefined
            }
            onChange={handleOrganizationChange}
          >
            {organizationOptions.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.nameTh}
              </MenuItem>
            ))}
          </FormSelect>
        </Flex>
      </Flex>

      <Flex sx={{ mt: 2 }} gap={2} width={'100%'}>
        <Flex justifyContent={'end'} minWidth="350px">
          <InputLabel
            sx={{
              textAlign: 'right',
              marginRight: 1,
              wordWrap: 'break-word',
              overflowWrap: 'break-word',
              whiteSpace: 'normal',
            }}
          >
            ชื่อองค์กรหรือสถาบันที่รับรองความเป็นกลางทางคาร์บอนของบริษัท
            (ภาษาไทย)
          </InputLabel>
        </Flex>

        <Flex width={'100%'}>
          <FormInput
            placeholder="กรุณาระบุรายชื่ิอ (ภาษาไทย)"
            name="organizationTh"
            sx={
              disabledInputOrganization || isCompletedTh
                ? { ...disabledFormInputStyles, width: '100%' }
                : { width: '100%' }
            }
          />
        </Flex>
      </Flex>

      <Flex sx={{ mt: 2 }} gap={2} width={'100%'}>
        <Flex justifyContent={'end'} minWidth="350px">
          <InputLabel
            sx={{
              textAlign: 'right',
              marginRight: 1,
              wordWrap: 'break-word',
              overflowWrap: 'break-word',
              whiteSpace: 'normal',
            }}
          >
            ชื่อองค์กรหรือสถาบันที่รับรองความเป็นกลางทางคาร์บอนของบริษัท
            (ภาษาอังกฤษ)
          </InputLabel>
        </Flex>

        <Flex width={'100%'}>
          <FormInput
            placeholder="กรุณาระบุรายชื่ิอ (ภาษาอังกฤษ)"
            name="organizationEn"
            sx={
              disabledInputOrganization || isCompletedEn
                ? { ...disabledFormInputStyles, width: '100%' }
                : { width: '100%' }
            }
          />
        </Flex>
      </Flex>
    </Flex>
  );
};

export default NeutralityVerification;
